import { Dispatch, SetStateAction } from 'react'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form'
import { Select, Input } from 'antd'
import style from '@ProdWb/components/ExceptionFailureModal/styles.css'
import { CloseOutlined, PaperClipOutlined } from '@ant-design/icons'

import {
    ExceptionFailureQuery,
    ExceptionFailureType,
    useExceptionFailureActionsQuery,
} from '~/gql/graphql'
import Text from '~/core/Text'
import Box from '~/core/Box'
import Link from '~/core/Link'
import UploadComponent from '~/components/FileUploadList/components/UploadComponent'
import {
    removeExceptionNoteAttachment,
    createAttachment,
    MutationOperations,
    handleExceptionAttachmentUpload,
} from '../logic'
import { NoteAttachmentType } from '../types'
import {
    ASSIGNEE_FILTERS_FOR_FAILURES,
    FORM_RULES,
    EXCEPTION_FORM_FIELDS,
} from '../constants'

const { Option } = Select
const { TextArea } = Input

type ExceptionActionsSectionPropsType = {
    form: WrappedFormUtils
    exception?: ExceptionFailureQuery['exceptionFailure']
    confirmFileUpload: (param: string) => void
    setNoteAttachment: Dispatch<SetStateAction<NoteAttachmentType[]>>
    noteAttachment: NoteAttachmentType[]
}

const ExceptionActionsSection = (props: ExceptionActionsSectionPropsType) => {
    const {
        form,
        form: { getFieldDecorator },
        exception,
        confirmFileUpload,
        setNoteAttachment,
        noteAttachment,
    } = props
    const { data } = useExceptionFailureActionsQuery()

    const actions = data?.exceptionFailureActions

    const mutationHandler = MutationOperations()

    return (
        <>
            <Form.Item
                label={
                    <Text fontSize="16px" color="blackAlpha85">
                        Exception action assignee
                    </Text>
                }
                className={style.formItemMargin}
                style={{ marginTop: '16px' }}
            >
                {getFieldDecorator(EXCEPTION_FORM_FIELDS.ASSIGNEE, {
                    initialValue: exception?.assignee,
                    rules:
                        form.getFieldValue(EXCEPTION_FORM_FIELDS.TYPE) ===
                        ExceptionFailureType.RFI
                            ? []
                            : FORM_RULES.assignee,
                })(
                    <Select
                        className={style.selectLayoutStyle}
                        placeholder="Select assignee"
                        getPopupContainer={(triggerNode: HTMLElement) =>
                            triggerNode.parentNode as HTMLElement
                        }
                    >
                        {ASSIGNEE_FILTERS_FOR_FAILURES?.map((option, index) => (
                            <Option value={option.VALUE} key={index}>
                                {option.LABEL}
                            </Option>
                        ))}
                    </Select>,
                )}
            </Form.Item>
            <Form.Item
                label={
                    <Text fontSize="16px" color="blackAlpha85">
                        Suggested action
                    </Text>
                }
                className={style.formItemMargin}
            >
                {getFieldDecorator(EXCEPTION_FORM_FIELDS.ACTION, {
                    initialValue: exception?.actionId,
                    rules:
                        form.getFieldValue(EXCEPTION_FORM_FIELDS.TYPE) ===
                        ExceptionFailureType.RFI
                            ? []
                            : FORM_RULES.action,
                })(
                    <Select
                        className={style.selectLayoutStyle}
                        placeholder="Select action"
                        getPopupContainer={(triggerNode: HTMLElement) =>
                            triggerNode.parentNode as HTMLElement
                        }
                    >
                        {actions?.map((option, index: number) => (
                            <Option value={option.id} key={index}>
                                {option.name}
                            </Option>
                        ))}
                    </Select>,
                )}
            </Form.Item>
            <Form.Item
                label={
                    <Text fontSize="16px" color="blackAlpha85">
                        Internal note/ attachment
                    </Text>
                }
                className={style.formItemMargin}
            >
                {getFieldDecorator(EXCEPTION_FORM_FIELDS.NOTE)(
                    <TextArea rows={4} placeholder="Type notes here" />,
                )}
            </Form.Item>
            <Box display="flexInline" flexWrap="wrap">
                {noteAttachment.map(attachment => (
                    <Box
                        direction="col"
                        key={attachment.id}
                        className={style.displayAttachmentStyle}
                    >
                        <Box>
                            <Link
                                data-test-target={`attachment_${attachment.id}_link`}
                                href={attachment.url}
                                target="_blank"
                                className={style.attachmentLinkStyle}
                            >
                                <PaperClipOutlined />{' '}
                                <Text>{attachment.fileName}</Text>
                            </Link>
                            <CloseOutlined
                                style={{
                                    cursor: 'pointer',
                                }}
                                onClick={() =>
                                    removeExceptionNoteAttachment(
                                        setNoteAttachment,
                                        noteAttachment,
                                        mutationHandler,
                                        attachment.id,
                                    )
                                }
                            />
                        </Box>
                    </Box>
                ))}
            </Box>
            <Box>
                <UploadComponent
                    listType="text"
                    onCreate={file =>
                        createAttachment(
                            file,
                            confirmFileUpload,
                            mutationHandler,
                        )
                    }
                    onSuccess={file =>
                        handleExceptionAttachmentUpload(
                            file,
                            setNoteAttachment,
                            confirmFileUpload,
                            mutationHandler,
                        )
                    }
                    uploadTitle="Upload attachment(s)"
                    multiple
                    layoutType
                />
            </Box>
        </>
    )
}

export default ExceptionActionsSection
